/* eslint-disable complexity */
'use client';

import {
	For,
	GridItem,
	SimpleGrid,
	Stack,
	Text,
} from '@chakra-ui/react';
import { FeatureContentTemplate } from '@/modules/shared';

import { APP_ROUTES } from '@/routes';
import { LoadingSpinner } from '@packages/chakra-v3';
import { ProductCard } from '../../components';
import YouTubePlayer from 'react-player/youtube';
import { useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useGetCollectionBySlugQuery } from '../../hooks/collections/queries';
import { DefaultStyle, ResponsiveAutoGrid } from '@packages/wastic-shared';

type ProductCollectionContainerProps = {
	collectionSlug: string;
};

const useProductCollection = (collectionSlug: string) => {
	const locale = useLocale() as 'en' | 'th';
	const isThai = locale === 'th';
	const { isLoading, data: collection } = useGetCollectionBySlugQuery(collectionSlug);

	const router = useRouter();

	const handleGoToProductDetail = (_collectionSlug: string, _productSlug: string) => {
		const url = APP_ROUTES.SHOP.PRODUCT_DETAILS.replace(':collectionSlug', _collectionSlug).replace(
			':productSlug',
			_productSlug,
		);
		router.push(url);
	};

	return {
		locale,
		isLoading,
		collection: {
			name:        collection?.name ?? '',
			videoUrl:    collection?.videoUrl ?? '',
			description: collection?.description ? (collection?.description as never)[locale] : '',
		},
		products: collection?.products
			? collection?.products.map(product => ({
				id:         product.id,
				name:       product.name,
				slug:       product.slug,
				coverImage: product.coverImage,
				price:      {
					amount:     isThai ? product.price : product.priceUsd,
					discounted: isThai ? product.discount?.discountPrice : product.discount?.discountPriceUsd,
					currency:   isThai ? 'THB' : 'USD',
				} as const,
				collection: { name: collection?.name ?? '' },
			}))
			:  [],
		handleGoToProductDetail,
	};
};

const ProductCollectionContainer = ({ collectionSlug }: ProductCollectionContainerProps) => {
	const {
		isLoading,
		collection,
		products,
		handleGoToProductDetail,
	} = useProductCollection(collectionSlug);

	if (isLoading) {
		return <LoadingSpinner />;
	}

	return (
		<Stack gap={DefaultStyle.CONTENT_GAP} w={'100%'}>
			<SimpleGrid
				alignItems={'center'}
				columns={{
					base: 1,
					xl:   2,
				}}
				gap={DefaultStyle.GRID_GAP}
			>
				<GridItem>
					<YouTubePlayer controls url={collection?.videoUrl ?? ''} width={'100%'} />
				</GridItem>
				<GridItem>
					<FeatureContentTemplate title={collection?.name}>
						<Text textStyle={'p2'} whiteSpace={'pre-wrap'}>{collection?.description}</Text>
					</FeatureContentTemplate>
				</GridItem>
			</SimpleGrid>
			<ResponsiveAutoGrid gap={DefaultStyle.GRID_GAP} >
				<For each={products}>
					{product => (
						<ProductCard
							key={product.id}
							product={product}
							onClickProductCard={() => handleGoToProductDetail(collectionSlug, product.slug)}
						/>
					)}
				</For>
			</ResponsiveAutoGrid>
		</Stack>
	);
};

export default ProductCollectionContainer;
